"use client";

import React, { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import LanguageSwitcher from "./LanguageSwitcher";
import { NAV_COPY, useSiteLocale } from "../utils/siteLocale";

// 햄버거 아이콘 컴포넌트 (모바일용)
const HamburgerIcon = ({ isWhiteBg }: { isWhiteBg: boolean }) => (
  <svg
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={`w-6 h-6 transition-colors duration-700 ${
      isWhiteBg
        ? "text-gray-900 hover:text-gray-600"
        : "text-white hover:text-gray-200"
    }`}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
    />
  </svg>
);

type GlobalNavProps = {
  toggleSidebar: () => void;
};

const GlobalNav: React.FC<GlobalNavProps> = ({ toggleSidebar }) => {
  const pathname = usePathname() ?? "/";

  // 플레이룸 페이지 체크 (실제 컨텐츠 페이지 - 화이트 배경 고정)
  const isPlayroomPage =
    (pathname === "/playroom" || pathname.startsWith("/playroom/")) &&
    !pathname.startsWith("/playroom/form/");
  // 플레이룸 하이드 페이지 (커밍순 페이지 - 투명 배경)
  const isPlayroomHidePage = pathname === "/playroom_hide";
  // 소셜링 페이지들 (투명 배경 유지)
  const isSocialingPage =
    pathname.startsWith("/socialing/love-buddies") ||
    pathname.startsWith("/socialing/game-orb");
  // 소셜링 메인 페이지 (스크롤에 따라 변경)
  const isSocialingMainPage = pathname === "/socialing";
  // 메인 홈 페이지
  const isHomePage = pathname === "/";
  // 프로젝트 페이지 (화이트 배경 고정)
  const isProjectPage = pathname.startsWith("/project");
  // 디자인 시스템 페이지 (다크 배경 고정)
  const isDesignSystemPage = pathname === "/design-system";
  // 플레이룸 폼 페이지 (다크 배경 고정)
  const isPlayroomFormPage = pathname.startsWith("/playroom/form/");
  const isNonKoreanPlaytestFormPage =
    pathname.startsWith("/playroom/form/playtest/en") ||
    pathname.startsWith("/playroom/form/playtest/ja") ||
    pathname.startsWith("/playroom/form/playtest/zh");
  // 플레이테스트 신청서는 자체 언어 전환(진행 내용 확인 팝업)이 있어 GNB 언어 메뉴를 숨긴다
  const isPlaytestFormPage = pathname.startsWith("/playroom/form/playtest");
  const [siteLocale] = useSiteLocale();
  const toolLinkLabel =
    isNonKoreanPlaytestFormPage || (!isPlaytestFormPage && siteLocale !== "ko")
      ? "ssobig tool"
      : "쏘빅툴";

  // 초기 상태를 false로 설정하여 서버/클라이언트 hydration 일치
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [initialViewportHeight, setInitialViewportHeight] = useState(0);
  const [isMounted, setIsMounted] = useState(false);

  // 마운트 상태 설정
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 초기 viewport 높이 저장 (모바일 브라우저 주소창 문제 해결)
  useEffect(() => {
    if (!isMounted) return;

    // 페이지 로드 시와 경로 변경 시 viewport 높이 재설정
    const updateViewportHeight = () => {
      setInitialViewportHeight(window.innerHeight);
    };

    updateViewportHeight();

    // 소셜링 메인 페이지 진입 시 초기 상태를 1섹션(흰 배경)으로 설정
    if (isSocialingMainPage) {
      setIsScrolled(true);
    } else if (isHomePage) {
      // 홈페이지는 항상 light 모드 (흰 배경, 검정 텍스트)
      setIsScrolled(true);
    } else if (isProjectPage) {
      // 프로젝트 페이지는 항상 light 모드 (흰 배경, 검정 텍스트)
      setIsScrolled(true);
    } else if (isPlayroomPage) {
      // 플레이룸 페이지는 항상 light 모드 (흰 배경, 검정 텍스트)
      setIsScrolled(true);
    } else if (
      !isPlayroomHidePage &&
      !isSocialingPage &&
      !isDesignSystemPage &&
      !isPlayroomFormPage
    ) {
      // 다른 페이지는 초기 상태를 false로
      setIsScrolled(false);
    }

    // orientationchange 이벤트도 처리 (태블릿 회전 대응)
    window.addEventListener("orientationchange", updateViewportHeight);
    return () =>
      window.removeEventListener("orientationchange", updateViewportHeight);
  }, [
    pathname,
    isSocialingMainPage,
    isPlayroomPage,
    isPlayroomHidePage,
    isSocialingPage,
    isHomePage,
    isProjectPage,
    isDesignSystemPage,
    isPlayroomFormPage,
    isMounted,
  ]);

  // 스크롤 감지 + 방향 감지
  useEffect(() => {
    if (!isMounted) return;

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          let currentScrollY = window.scrollY;

          // 소셜링 메인 페이지는 스냅 컨테이너의 스크롤 감지
          if (isSocialingMainPage) {
            const snapContainer = document.querySelector(".snap-container");
            if (snapContainer) {
              currentScrollY = snapContainer.scrollTop;
              // 1번째 섹션(화이트 배경)에 있는지 체크
              // 초기 viewport 높이를 사용하여 모바일 브라우저 주소창 영향 제거
              const viewportHeight =
                initialViewportHeight || window.innerHeight;
              // 1번 섹션(0 ~ 1 viewport)에 있으면 흰색 배경
              if (currentScrollY < viewportHeight * 0.95) {
                setIsScrolled(true);
              } else {
                setIsScrolled(false);
              }
            }
          } else if (isHomePage || isProjectPage || isPlayroomPage) {
            // 메인 홈 페이지 & 프로젝트 페이지 & 플레이룸 페이지: 항상 light 모드 (흰 배경, 검정 텍스트)
            setIsScrolled(true);
          } else {
            // 배경 변경 감지
            if (currentScrollY > 50) {
              setIsScrolled(true);
            } else {
              setIsScrolled(false);
            }
          }

          // GNB 표시/숨김 로직 (모바일만, 브랜드 메인 페이지는 항상 표시)
          const isMobile = window.innerWidth < 768;

          if (isSocialingMainPage) {
            // 브랜드 메인 페이지는 항상 GNB 표시
            document.body.classList.remove("gnb-hidden");
          } else if (isMobile) {
            if (currentScrollY < 10) {
              // 최상단에서는 항상 표시
              document.body.classList.remove("gnb-hidden");
            } else if (currentScrollY < lastScrollY) {
              // 스크롤 업 (위로) - 표시
              document.body.classList.remove("gnb-hidden");
            } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
              // 스크롤 다운 (아래로) - 숨김
              document.body.classList.add("gnb-hidden");
            }
          } else {
            // 데스크탑에서는 항상 표시
            document.body.classList.remove("gnb-hidden");
          }

          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    // 브랜드 메인 페이지는 스냅 컨테이너에 리스너 추가
    if (isSocialingMainPage) {
      const snapContainer = document.querySelector(".snap-container");
      if (snapContainer) {
        snapContainer.addEventListener("scroll", handleScroll, {
          passive: true,
        });
        return () => snapContainer.removeEventListener("scroll", handleScroll);
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    lastScrollY,
    isSocialingMainPage,
    isHomePage,
    isProjectPage,
    isPlayroomPage,
    initialViewportHeight,
    isMounted,
  ]);

  // 브랜드 메인 페이지 초기 로드 시 GNB 표시
  useEffect(() => {
    if (!isMounted) return;

    if (isSocialingMainPage) {
      document.body.classList.remove("gnb-hidden");
    }
  }, [isSocialingMainPage, isMounted]);

  // iOS Safari 주소창/뷰포트 변화 대응 & 데스크탑 전환 시 GNB 복구
  useEffect(() => {
    if (!isMounted) return;

    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      const currentScrollY = Math.max(0, window.scrollY);

      // 브랜드 메인 페이지는 항상 표시
      if (isSocialingMainPage) {
        document.body.classList.remove("gnb-hidden");
      } else if (!isMobile) {
        // 데스크탑으로 전환되면 항상 GNB 표시
        document.body.classList.remove("gnb-hidden");
      } else if (currentScrollY < 10) {
        // 모바일에서 최상단이면 표시
        document.body.classList.remove("gnb-hidden");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSocialingMainPage, isMounted]);

  // 쏘빅 오프라인 페이지 체크 (항상 다크 모드)
  const isOfflinePage = pathname?.startsWith("/offline");
  const isDarkStaticPage =
    isOfflinePage || isDesignSystemPage || isPlayroomFormPage;

  // 페이지 유형에 따른 네비게이션 모드 결정
  const navMode: "overlay" | "light" | "dark" = (() => {
    // 클라이언트가 마운트되기 전에는 안전한 기본 상태 사용
    if (!isMounted) {
      // 오프라인, 플레이룸 하이드, 소셜링 상세 페이지는 어두운 배경이므로 overlay로 시작
      if (isPlayroomFormPage) {
        return "dark";
      }
      if (isDarkStaticPage || isPlayroomHidePage || isSocialingPage) {
        return "overlay";
      }
      // 플레이룸 페이지는 항상 light 모드
      if (isPlayroomPage) {
        return "light";
      }
      return "light";
    }
    // 홈페이지는 항상 light 모드 (흰 배경, 검정 텍스트)
    if (isHomePage) {
      return "light";
    }
    // 플레이룸 페이지는 항상 light 모드 (흰 배경, 검정 텍스트)
    if (isPlayroomPage) {
      return "light";
    }
    // 오프라인/디자인 시스템 페이지는 항상 dark 모드 또는 overlay (스크롤 시 dark)
    if (isPlayroomFormPage) {
      return "dark";
    }
    if (isDarkStaticPage) {
      return isScrolled ? "dark" : "overlay";
    }
    if (isSocialingPage || isPlayroomHidePage) {
      return "overlay";
    }
    if (isSocialingMainPage) {
      return isScrolled ? "light" : "overlay";
    }
    if (isScrolled) {
      return "light";
    }
    return "overlay";
  })();

  const navBgClass =
    navMode === "light"
      ? "bg-white"
      : navMode === "dark"
      ? "bg-black backdrop-blur-md"
      : "";

  const useBlackText = navMode === "light";
  const useWhiteText = !useBlackText;
  const underlineColorClass = useBlackText ? "bg-gray-900" : "bg-white";

  return (
    <nav
      className={`global-nav transition-colors duration-700 ${navBgClass} relative`}
      style={{
        // iOS Safari 오버스크롤 대응
        WebkitOverflowScrolling: "touch",
        // 모바일에서 light 모드일 때 배경색 확실히 적용
        backgroundColor: navMode === "light" ? "#ffffff" : undefined,
      }}
    >
      {/* Light mode background - 모바일에서도 확실히 보이도록 */}
      {navMode === "light" && (
        <div className="absolute inset-0 bg-white pointer-events-none z-0" />
      )}
      {/* Overlay mode background layers - 모바일만 */}
      {navMode === "overlay" && (
        <>
          {/* 기본 블러 레이어 (5px) */}
          <div
            className="absolute inset-0 pointer-events-none md:hidden"
            style={{
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
            }}
          />
          {/* 추가 블러 레이어 (20px) - 그라데이션 마스크 적용 */}
          <div
            className="absolute inset-0 pointer-events-none md:hidden"
            style={{
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              maskImage:
                "linear-gradient(to bottom, black 0%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, black 0%, transparent 100%)",
            }}
          />
          {/* 색상 오버레이 레이어 */}
          <div
            className="absolute inset-0 pointer-events-none md:hidden"
            style={{
              background:
                "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%)",
            }}
          />
        </>
      )}
      {/* 모바일 버전 (md 미만) */}
      <div className="md:hidden relative z-20">
        {/* 1단: 로고 (52px) */}
        <div className="h-[52px] flex items-center justify-between px-5">
          <Link href="/" className="flex items-center relative">
            {/* 흰색 로고 */}
            <Image
              src="/ssobig_assets/Logo/logo=ssobig, color=white.png"
              alt="ssobig"
              width={176}
              height={65}
              className="transition-opacity duration-700"
              unoptimized
              style={{
                width: 80,
                height: (80 * 65) / 176,
                opacity: useWhiteText ? 1 : 0,
              }}
              suppressHydrationWarning
            />
            {/* 검은색 로고 */}
            <Image
              src="/ssobig_assets/Logo/logo=ssobig, color=black.png"
              alt="ssobig"
              width={176}
              height={65}
              className="absolute top-0 left-0 transition-opacity duration-700"
              unoptimized
              style={{
                width: 80,
                height: (80 * 65) / 176,
                opacity: useBlackText ? 1 : 0,
              }}
              suppressHydrationWarning
            />
          </Link>

          <div className="flex items-center">
            {!isPlaytestFormPage && (
              <LanguageSwitcher variant="mobile" isLight={useBlackText} />
            )}
            <button
              aria-label={NAV_COPY[siteLocale].open}
              className="p-2"
              onClick={toggleSidebar}
            >
              <HamburgerIcon isWhiteBg={useBlackText} />
            </button>
          </div>
        </div>

        {/* 2단: 메뉴 (36px) */}
        <div className="h-[36px] relative flex items-center justify-start gap-1 px-2 min-[390px]:gap-2 min-[390px]:px-3">
          <Link
            href="/"
            className="relative h-full flex items-center font-medium text-xs min-[390px]:text-sm"
          >
            <span
              className={`transition-all duration-700 ${
                useBlackText ? "text-gray-900" : "text-white"
              }`}
              style={{
                opacity: isMounted && pathname === "/" ? 1 : 0.6,
                color: navMode === "light" ? "#111827" : undefined,
              }}
              suppressHydrationWarning
            >
              HOME
            </span>
            {isMounted && pathname === "/" && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[1px] transition-colors duration-700 ${underlineColorClass}`}
                suppressHydrationWarning
              />
            )}
          </Link>

          <Link
            href="/playroom"
            className="relative h-full flex items-center font-medium text-xs min-[390px]:text-sm"
          >
            <span
              className={`transition-all duration-700 ${
                useBlackText ? "text-gray-900" : "text-white"
              }`}
              style={{
                opacity: isMounted && isPlayroomPage ? 1 : 0.6,
                color: navMode === "light" ? "#111827" : undefined,
              }}
              suppressHydrationWarning
            >
              PLAYROOM
            </span>
            {isMounted && isPlayroomPage && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[1px] transition-colors duration-700 ${underlineColorClass}`}
                suppressHydrationWarning
              />
            )}
          </Link>

          <Link
            href="/offline"
            className="relative h-full flex items-center font-medium text-xs min-[390px]:text-sm"
          >
            <span
              className={`transition-all duration-700 ${
                useBlackText ? "text-gray-900" : "text-white"
              }`}
              style={{
                opacity: isMounted && pathname === "/offline" ? 1 : 0.6,
                color: navMode === "light" ? "#111827" : undefined,
              }}
              suppressHydrationWarning
            >
              OFFLINE
            </span>
            {isMounted && pathname === "/offline" && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[1px] transition-colors duration-700 ${underlineColorClass}`}
                suppressHydrationWarning
              />
            )}
          </Link>

          <Link
            href="/project"
            className="relative h-full flex items-center font-medium text-xs min-[390px]:text-sm"
          >
            <span
              className={`transition-all duration-700 ${
                useBlackText ? "text-gray-900" : "text-white"
              }`}
              style={{
                opacity: isMounted && pathname === "/project" ? 1 : 0.6,
                color: navMode === "light" ? "#111827" : undefined,
              }}
              suppressHydrationWarning
            >
              PROJECT
            </span>
            {isMounted && pathname === "/project" && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[1px] transition-colors duration-700 ${underlineColorClass}`}
                suppressHydrationWarning
              />
            )}
          </Link>

          <a
            href="https://tool.ssobig.com/"
            target="_blank"
            rel="noopener noreferrer"
            className={`ml-auto h-[28px] px-2 min-[390px]:px-3 rounded-lg text-xs font-semibold whitespace-nowrap shrink-0 flex items-center gap-1 transition-all duration-700 ${
              useBlackText
                ? "bg-gray-900 text-white hover:bg-gray-700"
                : "bg-white text-gray-900 hover:bg-gray-200"
            }`}
          >
            {toolLinkLabel}
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </a>
        </div>
      </div>

      {/* 웹 버전 (md 이상) - 스크롤에 따라 변경 */}
      <div className="hidden md:flex items-center justify-between px-8 max-w-[1200px] mx-auto h-full relative z-10">
        {/* 로고 */}
        <Link href="/" className="flex items-center h-full">
          <Image
            src={
              useBlackText
                ? "/ssobig_assets/Logo/logo=ssobig, color=black.png"
                : "/ssobig_assets/Logo/logo=ssobig, color=white.png"
            }
            alt="ssobig"
            width={176}
            height={65}
            unoptimized
            style={{ width: 120, height: (120 * 65) / 176 }}
            suppressHydrationWarning
          />
        </Link>

        {/* 메뉴 */}
        <div className="flex items-center gap-6 h-full">
          <Link
            href="/"
            className="relative flex items-center h-full transition-opacity hover:opacity-80"
          >
            <span
              className={`font-medium text-base transition-colors duration-700 ${
                useBlackText ? "text-gray-900" : "text-white"
              }`}
              style={{
                opacity: isMounted && pathname === "/" ? 1 : 0.6,
              }}
              suppressHydrationWarning
            >
              HOME
            </span>
            {isMounted && pathname === "/" && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[1px] ${underlineColorClass}`}
                suppressHydrationWarning
              />
            )}
          </Link>
          <Link
            href="/playroom"
            className="relative flex items-center h-full transition-opacity hover:opacity-80"
          >
            <span
              className={`font-medium text-base transition-colors duration-700 ${
                useBlackText ? "text-gray-900" : "text-white"
              }`}
              style={{
                opacity: isMounted && isPlayroomPage ? 1 : 0.6,
              }}
              suppressHydrationWarning
            >
              PLAYROOM
            </span>
            {isMounted && isPlayroomPage && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[1px] ${underlineColorClass}`}
                suppressHydrationWarning
              />
            )}
          </Link>
          <Link
            href="/offline"
            className="relative flex items-center h-full transition-opacity hover:opacity-80"
          >
            <span
              className={`font-medium text-base transition-colors duration-700 ${
                useBlackText ? "text-gray-900" : "text-white"
              }`}
              style={{
                opacity: isMounted && pathname === "/offline" ? 1 : 0.6,
              }}
              suppressHydrationWarning
            >
              OFFLINE
            </span>
            {isMounted && pathname === "/offline" && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[1px] ${underlineColorClass}`}
                suppressHydrationWarning
              />
            )}
          </Link>
          <Link
            href="/project"
            className="relative flex items-center h-full transition-opacity hover:opacity-80"
          >
            <span
              className={`font-medium text-base transition-colors duration-700 ${
                useBlackText ? "text-gray-900" : "text-white"
              }`}
              style={{
                opacity: isMounted && pathname === "/project" ? 1 : 0.6,
              }}
              suppressHydrationWarning
            >
              PROJECT
            </span>
            {isMounted && pathname === "/project" && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[1px] ${underlineColorClass}`}
                suppressHydrationWarning
              />
            )}
          </Link>

          {!isPlaytestFormPage && (
            <>
              <span
                aria-hidden="true"
                className={`h-3.5 w-px transition-colors duration-700 ${
                  useBlackText ? "bg-gray-300" : "bg-white/30"
                }`}
              />
              <LanguageSwitcher variant="desktop" isLight={useBlackText} />
            </>
          )}

          <a
            href="https://tool.ssobig.com/"
            target="_blank"
            rel="noopener noreferrer"
            className={`ml-2 h-[32px] px-4 rounded-lg text-sm font-semibold whitespace-nowrap shrink-0 flex items-center gap-1.5 transition-all duration-700 ${
              useBlackText
                ? "bg-gray-900 text-white hover:bg-gray-700"
                : "bg-white text-gray-900 hover:bg-gray-200"
            }`}
          >
            {toolLinkLabel}
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </a>
        </div>
      </div>
    </nav>
  );
};

export default GlobalNav;
